/**
 * RSVP report breakdown utilities
 * Shared between frontend report tables and backend XLSX export.
 */

import type { RSVPLocationBreakdownItem } from '../../types/api';

export interface LocationSizeBreakdownGroup {
  date: string; // YYYY-MM-DD (service date)
  menuId: number;
  menuTitle: string;
  items: RSVPLocationBreakdownItem[];
  totals: {
    smallCount: number;
    mediumCount: number;
    largeCount: number;
    totalRsvps: number;
  };
}

/**
 * Group per-location breakdown rows by service day/menu, accumulating S/M/L
 * totals per group. Preserves input order (the report API returns rows
 * already sorted by date, then location).
 */
export function groupLocationSizeBreakdown(
  byLocation: RSVPLocationBreakdownItem[]
): LocationSizeBreakdownGroup[] {
  const groups = new Map<string, LocationSizeBreakdownGroup>();

  for (const item of byLocation) {
    const key = `${item.date}-${item.menuId}`;
    if (!groups.has(key)) {
      groups.set(key, {
        date: item.date,
        menuId: item.menuId,
        menuTitle: item.menuTitle,
        items: [],
        totals: { smallCount: 0, mediumCount: 0, largeCount: 0, totalRsvps: 0 },
      });
    }

    const group = groups.get(key)!;
    group.items.push(item);
    group.totals.smallCount += item.smallCount;
    group.totals.mediumCount += item.mediumCount;
    group.totals.largeCount += item.largeCount;
    group.totals.totalRsvps += item.totalRsvps;
  }

  return Array.from(groups.values());
}
